import { TimeElapsed } from '@missionlabs/smartagent-app-components';
import { SATableColumn } from '@missionlabs/smartagent-app-components/dist/Table';

import { format } from 'date-fns';
import React from 'react';

import { ChannelColumnGroup } from 'store/contactSearch/contactSearch.types';
import { IInteraction } from '../types';

import { formatPhoneNumber, getContactDurationSeconds } from 'utils';

import AttachmentsIcon from 'images/attachment.svg';

interface SATableColumnWithMappings extends SATableColumn {
    channels: ChannelColumnGroup[];
}

export const allColumnMappings: SATableColumnWithMappings[] = [
    {
        name: 'Channel',
        key: 'Channel',
        value: 'channel',
        sort: true,
        channels: [
            ChannelColumnGroup.VOICE,
            ChannelColumnGroup.EMAIL,
            ChannelColumnGroup.TASK,
            ChannelColumnGroup.CHAT,
            ChannelColumnGroup.SOCIAL,
        ],
    },
    {
        name: 'Attachments',
        key: 'Attachments',
        value: 'attributes.sa-message-attachment-count',
        sort: true,
        render: (messageAttachmentCount?: string) => {
            if (!messageAttachmentCount?.length) return <></>;
            return (
                <div className="contact-search-results-attachments">
                    <img src={AttachmentsIcon} alt="Attachments" />({messageAttachmentCount})
                </div>
            );
        },
        channels: [
            ChannelColumnGroup.EMAIL,
            ChannelColumnGroup.TASK,
            ChannelColumnGroup.CHAT,
            ChannelColumnGroup.SOCIAL,
        ],
    },
    {
        name: 'Queue',
        key: 'Queue',
        value: 'queueName',
        sort: true,
        clipFieldValues: true,
        channels: [
            ChannelColumnGroup.VOICE,
            ChannelColumnGroup.EMAIL,
            ChannelColumnGroup.TASK,
            ChannelColumnGroup.CHAT,
            ChannelColumnGroup.SOCIAL,
        ],
    },
    {
        name: 'Agent',
        key: 'Agent',
        path: '*',
        render: (interaction: IInteraction) =>
            interaction.agentName ?? interaction.attributes['sa-agent-name'] ?? 'No agent name found',
        clipFieldValues: true,
        channels: [
            ChannelColumnGroup.VOICE,
            ChannelColumnGroup.EMAIL,
            ChannelColumnGroup.TASK,
            ChannelColumnGroup.CHAT,
            ChannelColumnGroup.SOCIAL,
        ],
    },
    {
        name: 'Date',
        key: 'Date',
        value: 'initiationTimestamp',
        render: (timestamp?: number) => (timestamp ? format(timestamp, 'dd/MM/yyyy HH:mm') : 'Unknown'),
        sort: true,
        clipFieldValues: true,
        channels: [
            ChannelColumnGroup.VOICE,
            ChannelColumnGroup.EMAIL,
            ChannelColumnGroup.TASK,
            ChannelColumnGroup.CHAT,
            ChannelColumnGroup.SOCIAL,
        ],
    },
    {
        name: 'Initiation',
        key: 'Initiation',
        value: 'initiationMethod',
        sort: true,
        clipFieldValues: true,
        channels: [
            ChannelColumnGroup.VOICE,
            ChannelColumnGroup.EMAIL,
            ChannelColumnGroup.TASK,
            ChannelColumnGroup.CHAT,
            ChannelColumnGroup.SOCIAL,
        ],
    },
    {
        name: 'Duration',
        key: 'Duration',
        path: '*',
        render: (interaction: IInteraction) => {
            const fullDurationSeconds = getContactDurationSeconds(interaction);
            return (
                <TimeElapsed
                    hours={fullDurationSeconds > 3600}
                    minutes={fullDurationSeconds > 60}
                    date={TimeElapsed.from(fullDurationSeconds)}
                    annotate
                    noTick
                />
            );
        },
        sort: true,
        channels: [
            ChannelColumnGroup.VOICE,
            ChannelColumnGroup.EMAIL,
            ChannelColumnGroup.TASK,
            ChannelColumnGroup.CHAT,
            ChannelColumnGroup.SOCIAL,
        ],
    },
    {
        name: 'External ID',
        key: 'External ID',
        value: 'attributes.sa-externalID',
        sort: true,
        clipFieldValues: true,
        channels: [
            ChannelColumnGroup.VOICE,
            ChannelColumnGroup.EMAIL,
            ChannelColumnGroup.TASK,
            ChannelColumnGroup.CHAT,
            ChannelColumnGroup.SOCIAL,
        ],
    },
    {
        name: 'Telephone Number',
        key: 'Telephone Number',
        path: '*',
        render: (interaction: IInteraction) => {
            const phoneNumber: string =
                interaction.attributes['sa-dialled-number'] ?? interaction.customerEndpointAddress;
            return formatPhoneNumber(phoneNumber);
        },
        sort: true,
        clipFieldValues: true,
        channels: [ChannelColumnGroup.VOICE, ChannelColumnGroup.CHAT],
    },
    {
        name: 'Social Handle',
        key: 'Social Handle',
        path: '*',
        render: (interaction: IInteraction) => {
            if ([ChannelColumnGroup.CHAT].includes(interaction.channel as ChannelColumnGroup))
                return interaction.attributes['sa-customer-endpoint-address'];
        },
        sort: true,
        clipFieldValues: true,
        channels: [ChannelColumnGroup.SOCIAL],
    },
    {
        name: 'Unique Social ID',
        key: 'Unique Social ID',
        value: 'attributes.sa-customer-endpoint-id',
        sort: true,
        clipFieldValues: true,
        channels: [ChannelColumnGroup.SOCIAL],
    },
    {
        name: 'To/From Email',
        key: 'To/From Email',
        path: '*',
        render: (interaction: IInteraction) => {
            const email: string | undefined =
                interaction.attributes['sa-customer-email'] ??
                interaction.attributes['sa-customer-endpoint-address'] ??
                interaction.customerEndpointAddress;
            return email?.includes('@') ? email : '';
        },
        sort: true,
        clipFieldValues: true,
        channels: [ChannelColumnGroup.EMAIL, ChannelColumnGroup.TASK, ChannelColumnGroup.CHAT],
    },
    {
        name: 'CC Email',
        key: 'CC Email',
        value: 'attributes.sa-customer-cc-addresses',
        sort: true,
        clipFieldValues: true,
        channels: [ChannelColumnGroup.EMAIL, ChannelColumnGroup.TASK],
    },
    {
        name: 'Subject',
        key: 'Subject',
        path: '*',
        render: (interaction: IInteraction) => {
            // Find the corresponding email message and use its subject as the value
            const { messageId, emailMessages } = interaction;

            const emailMessage = emailMessages?.find(message => message.messageId === messageId);

            return emailMessage?.subject;
        },
        sort: true,
        clipFieldValues: true,
        channels: [ChannelColumnGroup.EMAIL, ChannelColumnGroup.TASK],
    },
];

type tableColumns = Record<ChannelColumnGroup, SATableColumn[]>;

const getTableColumns = (): tableColumns => {
    // dynamically create object with properties from enum
    const tableColumns: tableColumns = {} as tableColumns;
    Object.keys(ChannelColumnGroup).forEach(channel => (tableColumns[channel as ChannelColumnGroup] = []));
    // push the tagged columns to each property
    allColumnMappings.forEach((column: SATableColumnWithMappings) => {
        const { channels: _, ...columnWithoutChannels } = column;
        tableColumns[ChannelColumnGroup.ALL].push(columnWithoutChannels);
        column.channels.forEach(channel => {
            if (tableColumns[channel]) tableColumns[channel].push(columnWithoutChannels);
        });
    });

    return tableColumns;
};

export const allTableColumns: tableColumns = getTableColumns();
